import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Gift, CreditCard, Mail, Shield } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Home = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [siteContent, setSiteContent] = useState(null);

  useEffect(() => {
    fetchSiteContent();
  }, []);

  const fetchSiteContent = async () => {
    try {
      const response = await axios.get(`${API}/site-content`);
      setSiteContent(response.data);
    } catch (error) {
      console.error('Error fetching site content:', error);
      // Will fall back to i18n translations
    }
  };

  const getLangSuffix = () => {
    if (i18n.language === 'ru') return '-ru';
    if (i18n.language === 'kz') return '-kz';
    return '';
  };

  // Helper to get text - tries database first, falls back to i18n
  const getText = (dbField, i18nKey) => {
    if (siteContent && siteContent[dbField] && siteContent[dbField][i18n.language]) {
      return siteContent[dbField][i18n.language];
    }
    return t(i18nKey);
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar showAdmin={true} />
      
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-[#E3F2FD] via-white to-[#E3F2FD] py-12 md:py-24 overflow-hidden">
        <div className="absolute top-0 left-0 w-96 h-96 bg-[#1E88E5] opacity-5 rounded-full -translate-x-1/2 -translate-y-1/2"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-[#1E88E5] opacity-5 rounded-full translate-x-1/2 translate-y-1/2"></div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center">
            <div className="inline-block px-4 py-2 bg-white rounded-full shadow-md mb-4 md:mb-6">
              <span className="text-[#1E88E5] font-semibold text-xs sm:text-sm">{getText('hero_tag', 'home.heroTag')}</span>
            </div>
            
            <h1 className="text-4xl sm:text-5xl md:text-7xl font-bold text-black mb-4 md:mb-6 leading-tight px-2">
              {getText('hero_title', 'home.heroTitle')}
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-[#1E88E5] to-[#1565C0] overflow-visible pb-2">
                {getText('hero_subtitle', 'home.heroSubtitle')}
              </span>
            </h1>
            
            <p className="text-base sm:text-xl md:text-2xl text-gray-600 mb-8 md:mb-10 max-w-3xl mx-auto leading-relaxed px-4">
              {getText('hero_description', 'home.heroDescription')}
            </p>
            
            <button
              onClick={() => navigate(`/browse${getLangSuffix()}`)}
              className="px-8 py-4 md:px-10 md:py-5 bg-gradient-to-r from-[#1E88E5] to-[#1565C0] text-white text-lg md:text-xl font-bold rounded-xl hover:shadow-2xl transition-all transform hover:scale-105 shadow-lg hover:-translate-y-1 min-h-[48px]"
              data-testid="browse-gift-cards-btn"
            >
              {getText('hero_cta_button', 'home.browseCTA')}
            </button>
          </div>
        </div>
      </section>

      {/* Best Gift For Section */}
      <section className="py-12 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 md:mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-black mb-3 md:mb-4">{getText('best_gift_title', 'home.bestGiftForTitle')}</h2>
            <p className="text-lg md:text-xl text-gray-600">{getText('best_gift_subtitle', 'home.bestGiftForSubtitle')}</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
            <div className="group bg-gradient-to-br from-white to-[#E3F2FD] rounded-2xl p-8 text-center shadow-lg hover:shadow-2xl transition-all transform hover:-translate-y-2 border border-gray-100">
              <div className="w-20 h-20 bg-gradient-to-br from-[#1E88E5] to-[#1565C0] rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                <span className="text-4xl">🎓</span>
              </div>
              <h3 className="text-2xl font-bold text-black mb-2">{t('home.mastersStudents')}</h3>
              <p className="text-gray-600">{t('home.mastersDesc')}</p>
            </div>
            <div className="group bg-gradient-to-br from-white to-[#E3F2FD] rounded-2xl p-8 text-center shadow-lg hover:shadow-2xl transition-all transform hover:-translate-y-2 border border-gray-100">
              <div className="w-20 h-20 bg-gradient-to-br from-[#1E88E5] to-[#1565C0] rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                <span className="text-4xl">📚</span>
              </div>
              <h3 className="text-2xl font-bold text-black mb-2">{t('home.phdStudents')}</h3>
              <p className="text-gray-600">{t('home.phdDesc')}</p>
            </div>
            <div className="group bg-gradient-to-br from-white to-[#E3F2FD] rounded-2xl p-8 text-center shadow-lg hover:shadow-2xl transition-all transform hover:-translate-y-2 border border-gray-100">
              <div className="w-20 h-20 bg-gradient-to-br from-[#1E88E5] to-[#1565C0] rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                <span className="text-4xl">👨‍🏫</span>
              </div>
              <h3 className="text-2xl font-bold text-black mb-2">{t('home.lecturers')}</h3>
              <p className="text-gray-600">{t('home.lecturersDesc')}</p>
            </div>
            <div className="group bg-gradient-to-br from-white to-[#E3F2FD] rounded-2xl p-8 text-center shadow-lg hover:shadow-2xl transition-all transform hover:-translate-y-2 border border-gray-100">
              <div className="w-20 h-20 bg-gradient-to-br from-[#1E88E5] to-[#1565C0] rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                <span className="text-4xl">🔬</span>
              </div>
              <h3 className="text-2xl font-bold text-black mb-2">{t('home.researchers')}</h3>
              <p className="text-gray-600">{t('home.researchersDesc')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="how-it-works" className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center mb-12 text-black">{getText('how_it_works_title', 'home.howItWorksTitle')}</h2>
          <div className="grid md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-[#E3F2FD] rounded-full flex items-center justify-center mx-auto mb-4">
                <Gift className="w-8 h-8 text-[#1E88E5]" />
              </div>
              <h3 className="text-xl font-semibold mb-2 text-black">{getText('step1_title', 'home.step1Title')}</h3>
              <p className="text-gray-600">{getText('step1_desc', 'home.step1Desc')}</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-[#E3F2FD] rounded-full flex items-center justify-center mx-auto mb-4">
                <Mail className="w-8 h-8 text-[#1E88E5]" />
              </div>
              <h3 className="text-xl font-semibold mb-2 text-black">{getText('step2_title', 'home.step2Title')}</h3>
              <p className="text-gray-600">{getText('step2_desc', 'home.step2Desc')}</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-[#E3F2FD] rounded-full flex items-center justify-center mx-auto mb-4">
                <CreditCard className="w-8 h-8 text-[#1E88E5]" />
              </div>
              <h3 className="text-xl font-semibold mb-2 text-black">{getText('step3_title', 'home.step3Title')}</h3>
              <p className="text-gray-600">{getText('step3_desc', 'home.step3Desc')}</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-[#E3F2FD] rounded-full flex items-center justify-center mx-auto mb-4">
                <Shield className="w-8 h-8 text-[#1E88E5]" />
              </div>
              <h3 className="text-xl font-semibold mb-2 text-black">{getText('step4_title', 'home.step4Title')}</h3>
              <p className="text-gray-600">{getText('step4_desc', 'home.step4Desc')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-[#1E88E5] py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            {getText('cta_title', 'home.ctaTitle')}
          </h2>
          <p className="text-white text-lg mb-8 opacity-90">
            {getText('cta_subtitle', 'home.ctaSubtitle')}
          </p>
          <button
            onClick={() => navigate(`/browse${getLangSuffix()}`)}
            className="px-8 py-4 bg-white text-[#1E88E5] text-lg font-semibold rounded-lg hover:bg-gray-100 transition-all transform hover:scale-105 shadow-lg"
          >
            {getText('cta_button', 'home.ctaButton')}
          </button>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Home;
